import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import CreateCompanyModal from '../components/auth/CreateCompanyModal'
import SupabaseTest from '../components/SupabaseTest'
import { Building, Users, FolderOpen, Plus } from 'lucide-react'

interface Area {
  id: string
  name: string
  company_id: string
  created_at: string
}

export default function HomePage() {
  const { profile, currentCompany, userRole, companies } = useAuth()
  const [areas, setAreas] = useState<Area[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false)

  useEffect(() => {
    if (currentCompany) {
      loadAreas()
    } else {
      setLoading(false)
    }
  }, [currentCompany])

  const loadAreas = async () => {
    if (!currentCompany) return

    try {
      const { data, error } = await supabase
        .from('areas')
        .select('*')
        .eq('company_id', currentCompany.id)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Erro ao carregar áreas:', error)
        return
      }

      setAreas(data || [])
    } catch (error) {
      console.error('Erro ao carregar áreas:', error)
    } finally {
      setLoading(false)
    }
  }
  // Se não tem empresa, mostrar tela de boas-vindas
  if (!currentCompany) {
    return (
      <div className="min-h-screen page-bg">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <Building className="h-16 w-16 text-blue-600 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Bem-vindo, {profile?.username || 'Usuário'}!
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg mb-8">
                Para começar, você precisa criar uma empresa ou ser convidado para uma.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <div className="card text-center">
                <Building className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Criar Empresa
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Crie sua própria empresa e comece a gerenciar processos.
                </p>
                <button 
                  onClick={() => setShowCreateCompanyModal(true)}
                  className="btn-primary w-full"
                >
                  Criar Empresa
                </button>
              </div>

              <div className="card text-center">
                <Users className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Aguardar Convite
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Aguarde ser convidado para uma empresa existente.
                </p>
                <button className="btn-ghost w-full" disabled>
                  Aguardando...
                </button>
              </div>
            </div>

            {companies.length > 0 && (
              <div className="mt-12">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Suas empresas:
                </h3>
                <div className="flex flex-wrap gap-2 justify-center">
                  {companies.map((company) => (
                    <button
                      key={company.id}
                      onClick={() => window.location.reload()}
                      className="btn-ghost"
                    >
                      {company.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen page-bg">
      <div className="container mx-auto px-4 py-12">
        {/* Header com informações da empresa */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <Building className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {currentCompany.name}
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Bem-vindo, {profile?.username || 'Usuário'} • {userRole === 'owner' ? 'Proprietário' : userRole === 'admin' ? 'Administrador' : 'Colaborador'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {areas.length}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Áreas
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              placeholder="Pesquisar processos, áreas ou palavras-chave..."
              className="input w-full px-6 py-4 text-lg rounded-xl shadow-sm"
            />
            <button className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-gray-900 dark:bg-gray-700 text-white px-6 py-2 rounded-lg hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors">
              Buscar
            </button>
          </div>
        </div>

        {/* Areas Section */}
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Áreas</h2>
            {areas.length > 6 && (
              <Link 
                to="/areas" 
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              >
                Ver todas
              </Link>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {areas.slice(0, 6).map((area) => (
                <Link 
                  key={area.id}
                  to={`/areas/${area.id}`}
                  className="card hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md dark:hover:shadow-gray-700/30 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <FolderOpen className="h-5 w-5 text-blue-600" />
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Área</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {area.name}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Criada em {new Date(area.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </Link>
              ))}

              {/* Add New Area Card */}
              {(userRole === 'owner' || userRole === 'admin') && (
                <Link 
                  to="/novo"
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 flex flex-col items-center justify-center min-h-[140px] group"
                >
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3 group-hover:bg-blue-100 dark:group-hover:bg-blue-800 transition-colors">
                    <Plus className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400" />
                  </div>
                  <span className="text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 font-medium">Adicionar Área</span>
                </Link>
              )}
            </div>
          )}

          {areas.length === 0 && !loading && (
            <div className="text-center py-12">
              <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Nenhuma área encontrada
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Comece criando sua primeira área de processo.
              </p>
              {(userRole === 'owner' || userRole === 'admin') && (
                <Link to="/novo" className="btn-primary">
                  Criar Primeira Área
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Teste de Conexão Supabase - TEMPORÁRIO */}
        <div className="max-w-6xl mx-auto mb-12">
          <SupabaseTest />
        </div>

        {/* Footer */}
        <div className="text-center mt-16">
          <p className="text-gray-500 dark:text-gray-400 text-sm">© 2024 Lamyda</p>
        </div>

        {/* Create Company Modal */}
        <CreateCompanyModal
          isOpen={showCreateCompanyModal}
          onClose={() => setShowCreateCompanyModal(false)}
          onSuccess={() => {
            // Recarregar a página para atualizar o contexto
            window.location.reload()
          }}
        />
      </div>
    </div>
  )
}
